import axios from 'axios'
export default class LocationUtilities {
    /**
     * Method generates the needed data for the header of all our requests
     * @returns an object with the needed data to process the header of our requests
     */
    static getRequestHeaders = () => {
        return {
            headers: {
                'X-Region': window.globalThis.region,
            },
        }
    }

    /**
     * Method generates a filters object that includes location and event filters
     * @returns an object with the needed data to correctly filter the location search request
     */
    static generateSearchFilters = filters => {
        if (!filters?.length) {
            return {
                filters: [],
                eventSearchFilters: undefined,
            }
        }

        if (filters?.some(filter => filter?.isMobileFittingFilter || filter?.isEventFilter)) {
            return filters?.reduce((acc, currentFilter) => {
                if (currentFilter?.isMobileFittingFilter || currentFilter?.isEventFilter) {
                    if (!acc.eventSearchFilters) {
                        acc.eventSearchFilters = {
                            displayOnMap: true,
                            insideAdvancedBookingWindow: true,
                        }
                    }

                    if (currentFilter?.isMobileFittingFilter) {
                        acc.eventSearchFilters = {
                            ...acc.eventSearchFilters,
                            mobileFitting: true,
                        }
                    } else {
                        acc.eventSearchFilters.eventTypes = acc.eventSearchFilters.eventTypes?.concat(currentFilter.value) || [currentFilter.value]
                    }
                } else {
                    acc.filters = acc.filters?.concat(currentFilter.value) || [currentFilter.value]
                }

                return acc
            }, {})
        } else {
            return {
                filters: filters.map(filter => filter.value),
                eventSearchFilters: undefined,
            }
        }
    }

    /**
     * Method generates the needed data for the body of the postSearchLocation method
     * @returns an object with the needed data to process the search request
     */
    static generateSearchBody = searchLocationData => {
        return {
            latitude: searchLocationData?.mapCenter?.lat,
            longitude: searchLocationData?.mapCenter?.lng,
            sortBy: searchLocationData?.sortBy,
            radius: searchLocationData?.radius,
            unitType: searchLocationData?.unitType,
            ...this.generateSearchFilters(searchLocationData?.appliedFilters),
        }
    }

    /**
     * Method generates the needed url by google maps to open up a driving directions screen with a set destination
     * @returns a string with a google maps driving directions url of the given destination
     */
    static generateDrivingDirectionsUrl = (lat, lng) => {
        const encodedQuery = encodeURIComponent(`${lat},${lng}`)
        return `https://www.google.com/maps/dir/?api=1&destination=${encodedQuery}`
    }

    /**
     * Method retrieves the locations that match the applied request body
     * @returns an array of locations that match the applied criteria
     */
    static postSearchLocations = (searchBody, signal = null) => {
        const endpoint = process.env.REACT_APP_FM_LOCATIONS_URL + process.env.REACT_APP_FM_LOCATIONS_SEARCH_PATH
        const config = {
            ...this.getRequestHeaders(),
            ...(signal && { signal })
        }
        return axios.post(endpoint, searchBody, config)
    }

    /**
     * Method retrieves single location object with address, hours, certs, technology, environment
     * @returns a single location object with entire data
     */
    static getLocationData = locationId => {
        const endpoint = process.env.REACT_APP_FM_LOCATIONS_URL + process.env.REACT_APP_FM_LOCATIONS_SINGLE_LOCATIONS_PATH
        return axios.get(endpoint.replace('{id}', locationId), this.getRequestHeaders())
    }

    /**
     * Method retrieves all available filters that could be applied to a search
     * @returns an array of filter categories
     */
    static getFilters = () => {
        const endpoint = process.env.REACT_APP_FM_LOCATIONS_URL + process.env.REACT_APP_FM_FILTERS_PATH
        return axios.get(endpoint, this.getRequestHeaders())
    }

    /**
     * Method retrieves all event types
     * @returns an array of event types
     */
    static getEventTypes = () => {
        const endpoint = process.env.REACT_APP_FM_LOCATIONS_URL + process.env.REACT_APP_FM_EVENT_TYPES_PATH
        return axios.get(`${endpoint}?filterable=true`, this.getRequestHeaders())
    }
}
